import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, CheckCircle, Cloud, Copy, Loader2, Play, Radio, RefreshCw, Search, Trash2, Unlink, UserPlus } from 'lucide-react';
import {
  AccountType,
  BucketDelivery,
  DeliveryAccount,
  DeliveryRun,
  DeltaRun,
  DestinationMode,
  EnableRequest,
  PrepareRequest,
  bucketDeliveryApi,
} from '@/services/bucketDelivery';

const POLL_MS = 8000;        // something is in progress: provisioning, a run, an IAM change
const IDLE_POLL_MS = 60000;  // nothing pending: still notice a delta, a requested run, a deletion, an error

const ACCOUNT_TYPES: { value: AccountType; label: string; placeholder: string }[] = [
  { value: 'serviceAccount', label: 'Service account', placeholder: 'loader@your-project.iam.gserviceaccount.com' },
  { value: 'user', label: 'Google account', placeholder: 'name@company.com' },
  { value: 'group', label: 'Google group', placeholder: 'data-team@company.com' },
];

function formatDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * How often to refresh, or null for states nothing moves on its own (codex review of #6944): an
 * enabled site keeps refreshing while idle, or an open tab would never show the next hourly delta, nor
 * a full export someone else requested; so does a disabled site until its bucket is deleted, so a
 * failed deletion shows up.
 */
function pollDelay(d: BucketDelivery | null): number | null {
  if (!d) return null;
  if (d.status === 'provisioning' || d.activeRun) return POLL_MS;
  if ((d.accounts || []).some(a => a.iamStatus === 'pending_add' || a.iamStatus === 'pending_remove')) return POLL_MS;
  // Phase 2: a delta running, a detach waiting for the worker, a client destination not checked yet.
  if (d.pubsub?.activeDelta || (d.pubsub?.pendingDetaches || []).length > 0) return POLL_MS;
  if (d.status === 'enabled' && d.client && !d.client.verifiedAt && !d.client.error) return POLL_MS;
  // Phase 2.1: a Check now waiting for the worker's next tick.
  if (d.status === 'enabled' && d.client?.checkRequestedAt) return POLL_MS;
  // Phase 2.3: the worker is creating the store's account, or a fresh grant is still reaching Google.
  if ((d.status === 'preparing' || d.status === 'enabled') && d.client?.serviceAccountState === 'creating') return POLL_MS;
  if (d.status === 'enabled' && d.client?.propagating) return POLL_MS;
  // Phase 2.1: a preparing destination changes only when the user acts (nothing is checked).
  if (d.status === 'preparing') return null;
  if (d.status === 'enabled') return IDLE_POLL_MS;
  if (d.status === 'disabled' && !d.bucketDeletedAt) return IDLE_POLL_MS;
  return null;
}

function AccountStatusBadge({ account }: { account: DeliveryAccount }) {
  switch (account.iamStatus) {
    case 'applied':
      return <Badge variant="success">Active</Badge>;
    case 'pending_add':
      return <Badge variant="secondary">Granting…</Badge>;
    case 'pending_remove':
      return <Badge variant="secondary">Removing…</Badge>;
    default:
      return <Badge variant="destructive">Refused by Google</Badge>;
  }
}

function RunStatusLine({ run }: { run: DeliveryRun }) {
  const label = {
    queued: 'Queued',
    running: 'Running',
    failed_retryable: 'Waiting to retry',
    completed: 'Completed',
    failed: 'Failed',
  }[run.status];
  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-center gap-2">
        {run.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          · {run.products.toLocaleString()} products in {run.files} file{run.files === 1 ? '' : 's'} so far
          {run.attempts > 1 ? ` · attempt ${run.attempts}` : ''}
        </span>
      </div>
      {run.error && <p className="text-destructive break-words">Last error: {run.error}</p>}
      {run.nextAttemptAt && <p className="text-muted-foreground">Next attempt: {formatDate(run.nextAttemptAt)}</p>}
    </div>
  );
}

function AccountRow({ account, disabled, onRemove }: { account: DeliveryAccount; disabled: boolean; onRemove: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b pb-3 last:border-b-0">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm break-all">{account.email}</span>
          <Badge variant="outline">{ACCOUNT_TYPES.find(t => t.value === account.type)?.label || account.type}</Badge>
          <AccountStatusBadge account={account} />
        </div>
        {account.iamError && <p className="text-sm text-destructive break-words">{account.iamError}</p>}
      </div>
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Remove ${account.email}`}
        disabled={disabled || account.iamStatus === 'pending_remove'}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function CodeLine({ text, onCopy, copied }: { text: string; onCopy: () => void; copied: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 bg-muted px-3 py-2 rounded-md font-mono text-sm break-all">{text}</code>
      <Button size="sm" variant="ghost" onClick={onCopy} aria-label={copied ? `Copied ${text}` : `Copy ${text}`}>
        {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

function DeltaStatusLine({ run, label }: { run: DeltaRun; label: string }) {
  const status = {
    queued: 'queued',
    running: 'publishing',
    failed_retryable: 'waiting to retry',
    completed: 'completed',
    failed: 'failed',
  }[run.status];
  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {run.status === 'running' && <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="font-medium">{label}:</span>
        <span>
          {status} · {run.messagesPublished.toLocaleString()} message{run.messagesPublished === 1 ? '' : 's'}
          {run.finishedAt ? ` · ${formatDate(run.finishedAt)}` : ''}
          {run.governance.refused > 0 ? ` · ${run.governance.refused.toLocaleString()} withheld by the publication guard` : ''}
          {run.attempts > 1 ? ` · attempt ${run.attempts}` : ''}
        </span>
      </div>
      {run.error && <p className="text-destructive break-words">Last error: {run.error}</p>}
    </div>
  );
}

/** The folder prefix as it will be stored (a trailing "/" added), for the notification line under the form. */
function notificationPrefix(prefix: string): string {
  const p = prefix.trim();
  return p === '' || p.endsWith('/') ? p : `${p}/`;
}

/**
 * Phase 2.4 (coordinator decision): Cloud Storage notifications cannot be created in the console, so the client
 * gets the command, filled in from its destination. `<your-topic>` stays a placeholder: the topic is theirs and
 * we do not know it. In the form, a field not typed yet shows as `<bucket>` / `<project>` (what to fill in).
 */
const NOTIFICATION_WHERE = '(notifications are created with gcloud or the API, not in the console)';
function notificationCommand(bucket: string, projectId: string, prefix: string): string {
  return `gcloud storage buckets notifications create gs://${bucket.trim() || '<bucket>'} --topic=projects/${projectId.trim() || '<project>'}/topics/<your-topic> --event-types=OBJECT_FINALIZE --object-prefix=${notificationPrefix(prefix)}full/`;
}

/**
 * Phase 2.4 (finding 4): what the store gets, in one paragraph, at the top of the destination card before any
 * step. Client mode without a topic gets no changes, so its overview does not promise them.
 */
function clientOverview(hasTopic: boolean): string {
  return hasTopic
    ? "What you get: your bucket receives a complete copy of this store's enriched catalogue as compressed JSON Lines files each time you request a full export, and your topic receives one message per changed product every hour. You read both with your own tools; we never read anything in your project."
    : "What you get: your bucket receives a complete copy of this store's enriched catalogue as compressed JSON Lines files each time you request a full export. You read them with your own tools; we never read anything in your project.";
}
const HOSTED_OVERVIEW = "What you get: a private bucket and two topics in our project, which you read with the accounts you add below. The bucket receives a complete copy of this store's enriched catalogue as compressed JSON Lines files each time you request a full export, the files topic announces each new file, and the changes topic receives one message per changed product every hour.";

/**
 * Phase 2.4 (finding 6): the two things a consumer of the CHANGES topic must know first, at the top of the reading
 * card (hosted: How to read the bucket; client: What we write). Scoped to the changes topic (PR #7141 review):
 * file notifications carry bucketId/objectId/objectGeneration, not payload_hash or run_seq; see FILE_NOTIFICATIONS.
 */
function ConsumerNotes({ changes }: { changes: boolean }) {
  if (!changes) return null;
  return (
    <div className="space-y-1 font-medium">
      <p>
        Messages on the changes topic can arrive more than once and in any order: skip a <code>payload_hash</code> you already
        have, keep the highest <code>run_seq</code> per product.
      </p>
      <p>Changes carry additions and updates only. To drop products you no longer sell, request a full export and replace your copy.</p>
    </div>
  );
}

/** How to deduplicate file notifications (PR #7141 review): hosted, our files topic; client, the client's own notification. */
const FILES_TOPIC_NOTIFICATIONS = 'Messages on the files topic are standard Cloud Storage notifications: each names the new file by bucketId, objectId and objectGeneration; a repeat of the same objectId and objectGeneration is the same file, process it once.';
const CLIENT_FILE_NOTIFICATIONS = 'Messages of that notification are standard Cloud Storage notifications: each names the new file by bucketId, objectId and objectGeneration; a repeat of the same objectId and objectGeneration is the same file, process it once.';

/** Pre-filled values of the form: the previous destination, after Disable (phase 2.1, decision 5). */
type EnableFormInitial = { mode: DestinationMode; projectId?: string; bucket?: string; prefix?: string; topic?: string | null };

/**
 * The two destinations a store can choose when it enables synchronization (spec item 13). Hosted is
 * enabled in one click; the client's own project is PREPARED (phase 2.1, #7015): stored and shown with
 * its setup, and checked only once the user clicks Check and enable.
 */
function EnableForm({ busy, onEnable, onPrepare, initial }: {
  busy: string | null;
  onEnable: (body: EnableRequest) => void;
  onPrepare: (body: PrepareRequest) => void;
  initial?: EnableFormInitial;
}) {
  const [mode, setMode] = useState<DestinationMode>(initial?.mode || 'hosted');
  const [projectId, setProjectId] = useState(initial?.projectId || '');
  const [bucket, setBucket] = useState(initial?.bucket || '');
  const [prefix, setPrefix] = useState(initial?.prefix || '');
  const [topic, setTopic] = useState(initial?.topic || '');
  return (
    <div className="space-y-4">
      <RadioGroup value={mode} onValueChange={(v) => setMode(v as DestinationMode)} aria-label="Destination">
        <div className="flex items-start gap-2">
          <RadioGroupItem value="hosted" id="bucket-mode-hosted" />
          <Label htmlFor="bucket-mode-hosted" className="space-y-1">
            <span className="block font-medium">PageMind hosts the bucket</span>
            <span className="block text-sm font-normal text-muted-foreground">
              We create a private Requester Pays bucket and Pub/Sub topics in our Google Cloud project, and grant the accounts you add.
            </span>
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem value="client" id="bucket-mode-client" />
          <Label htmlFor="bucket-mode-client" className="space-y-1">
            <span className="block font-medium">Deliver to your Google Cloud project</span>
            <span className="block text-sm font-normal text-muted-foreground">
              We write the files into your bucket and publish changed products to your Pub/Sub topic.
            </span>
          </Label>
        </div>
      </RadioGroup>
      {mode === 'client' && (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="client-project">Project id</Label>
              <Input id="client-project" value={projectId} placeholder="your-project-id" onChange={(e) => setProjectId(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-bucket">Bucket</Label>
              <Input id="client-bucket" value={bucket} placeholder="your-bucket" onChange={(e) => setBucket(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                An existing bucket in the project above. We write under the folder prefix; create the bucket first.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-prefix">Folder prefix (optional)</Label>
              <Input id="client-prefix" value={prefix} placeholder="pagemind/store/" onChange={(e) => setPrefix(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Optional folder inside the bucket, ending with <code>/</code>. Leave empty to write at the root.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="client-topic">Changes topic (Pub/Sub, optional)</Label>
              <Input id="client-topic" value={topic} placeholder="projects/your-project-id/topics/your-topic" onChange={(e) => setTopic(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Full name of an existing topic in your project, <code>{'projects/<project>/topics/<topic>'}</code>. Create it first
                (Pub/Sub › Topics › Create topic). Optional: without it you get files on request only, no hourly changes.
              </p>
            </div>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            <p>
              Your own bucket notification is up to you: to be told about each new file, create a notification on{' '}
              <code>{`${notificationPrefix(prefix)}full/`}</code> of your bucket to a topic of yours {NOTIFICATION_WHERE}:
            </p>
            <code className="block bg-muted px-3 py-2 rounded-md font-mono break-all select-all">{notificationCommand(bucket, projectId, prefix)}</code>
          </div>
          <p className="text-sm text-muted-foreground">
            Without a topic you receive a full export each time you request one. With one, we also publish every changed product
            every hour. Prepare creates this store's own Google service account and shows the two roles to grant it on your bucket
            and topic. Nothing is checked, written or published until you then click Check and enable.
          </p>
          <p className="text-sm text-muted-foreground">We do not accept key files; grant our account on your resources instead.</p>
        </div>
      )}
      {mode === 'client' ? (
        <Button
          onClick={() => onPrepare({ mode: 'client', project_id: projectId.trim(), bucket: bucket.trim(), prefix: prefix.trim(), topic: topic.trim() || null })}
          disabled={busy !== null}
        >
          {busy === 'prepare' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
          Prepare
        </Button>
      ) : (
        <Button onClick={() => onEnable({ mode: 'hosted' })} disabled={busy !== null}>
          {busy === 'enable' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
          Enable Google bucket synchronization
        </Button>
      )}
    </div>
  );
}

/**
 * Keyed by site: switching sites mounts a fresh tab, so a late response to a request made for the
 * previous site (a GET, or a mutation that was still in flight) can never land in the new site's
 * view (codex review of #6944).
 */
export function GoogleBucketTab({ siteId }: { siteId: string }) {
  return <SiteBucketTab key={siteId} siteId={siteId} />;
}

function SiteBucketTab({ siteId }: { siteId: string }) {
  const { toast } = useToast();
  const [delivery, setDelivery] = useState<BucketDelivery | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [accountType, setAccountType] = useState<AccountType>('serviceAccount');
  const [accountEmail, setAccountEmail] = useState('');
  const [accountError, setAccountError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Phase 2.1 (decision 5): "Enable again" on a turned-off client destination opens the pre-filled form.
  const [reEnableOpen, setReEnableOpen] = useState(false);
  const loadAbort = useRef<AbortController | null>(null);
  // Bumped when a mutation starts and again when it ends (codex review of #6944). A status read
  // begun before either answers for an older state: it is dropped, so a slow poll can never put
  // back what a mutation just changed (an account, a run, the site turned off).
  const epoch = useRef(0);
  // False once the user has switched site (this instance is unmounted): a late answer to one of its
  // requests must not toast over, or write into, the other site's tab.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true; // again after React StrictMode's dev-only unmount/remount
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    loadAbort.current?.abort();
    const controller = new AbortController();
    loadAbort.current = controller;
    const startedAt = epoch.current;
    const stale = () => controller.signal.aborted || epoch.current !== startedAt;
    try {
      const { delivery: next } = await bucketDeliveryApi.get(siteId, controller.signal);
      if (stale()) return;
      setDelivery(next);
      setLoadError(null);
    } catch (err: any) {
      if (err?.name === 'AbortError' || stale()) return;
      setLoadError(err?.message || String(err));
    }
  }, [siteId]);

  const beginMutation = () => {
    epoch.current += 1;
    loadAbort.current?.abort();
  };
  const endMutation = () => {
    epoch.current += 1;
  };

  useEffect(() => {
    setDelivery(null);
    setLoadError(null);
    load();
    return () => loadAbort.current?.abort();
  }, [load]);

  // An interval keyed on the cadence, not re-armed by each answer: a read that is aborted or dropped
  // (see epoch) changes no state, and must not stop the refreshing.
  const delay = pollDelay(delivery);
  useEffect(() => {
    if (delay === null) return;
    const interval = setInterval(load, delay);
    return () => clearInterval(interval);
  }, [delay, load]);

  // "Request full export" is disabled until fullSyncAllowedAt: refresh right after it, so the button comes
  // back on time instead of at the next idle poll.
  useEffect(() => {
    if (!delivery || delivery.status !== 'enabled' || !delivery.fullSyncAllowedAt) return;
    const wait = new Date(delivery.fullSyncAllowedAt).getTime() - Date.now();
    if (!(wait > 0) || wait > IDLE_POLL_MS * 60 * 24) return;
    const timer = setTimeout(load, wait + 1000);
    return () => clearTimeout(timer);
  }, [delivery, load]);

  const act = async (name: string, fn: () => Promise<{ delivery: BucketDelivery }>, success?: string) => {
    setBusy(name);
    beginMutation();
    try {
      const { delivery: next } = await fn();
      endMutation();
      if (!mounted.current) return false;
      setDelivery(next);
      setLoadError(null);
      if (success) toast({ title: success });
      return true;
    } catch (err: any) {
      endMutation();
      if (!mounted.current) return false;
      toast({ title: 'Google bucket', description: err?.message || String(err), variant: 'destructive' });
      return false;
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const removeAccount = (account: DeliveryAccount) =>
    act(`remove-${account.principal}`, () => bucketDeliveryApi.removeAccount(siteId, account.principal));

  // The check mark means the text IS in the clipboard (codex review of #6944): it shows only once the
  // write resolved. No clipboard (an insecure origin, an old browser) or a refused write says so.
  const copy = async (text: string, id: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('This browser gives the page no clipboard access. Select the text and copy it by hand.');
      await navigator.clipboard.writeText(text);
    } catch (err: any) {
      if (mounted.current) toast({ title: 'Copy failed', description: err?.message || String(err), variant: 'destructive' });
      return;
    }
    if (!mounted.current) return;
    setCopiedId(id);
    setTimeout(() => { if (mounted.current) setCopiedId(null); }, 2000);
  };

  const addAccount = async () => {
    const email = accountEmail.trim();
    if (!email) {
      setAccountError('Enter an email address.');
      return;
    }
    setAccountError(null);
    setBusy('add-account');
    beginMutation();
    try {
      const { delivery: next } = await bucketDeliveryApi.addAccount(siteId, `${accountType}:${email}`);
      endMutation();
      if (!mounted.current) return;
      setDelivery(next);
      setAccountEmail('');
    } catch (err: any) {
      endMutation();
      if (!mounted.current) return;
      setAccountError(err?.message || String(err));
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  if (loadError && !delivery) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Could not load the Google bucket settings: {loadError}{' '}
          <Button variant="link" className="h-auto p-0" onClick={load}>Try again</Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!delivery) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  // A refresh that fails after a successful load must not leave a stale view looking current.
  const view = (node: React.ReactNode) => (loadError ? (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Could not refresh the Google bucket status: {loadError}. What is shown below may be out of date.{' '}
          <Button variant="link" className="h-auto p-0" onClick={load}>Try again</Button>
        </AlertDescription>
      </Alert>
      {node}
    </div>
  ) : node);

  const limits = delivery.limits;
  const mode: DestinationMode = delivery.destinationMode || 'hosted';
  const enableWith = (body: EnableRequest) => act('enable', () => bucketDeliveryApi.enable(siteId, body));
  const prepareWith = (body: PrepareRequest) => act('prepare', () => bucketDeliveryApi.prepare(siteId, body));

  if (delivery.status === 'not_enabled') {
    return view(
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Cloud className="h-5 w-5" /> Google bucket synchronization</CardTitle>
          <CardDescription>
            We write your complete enriched catalogue to Google Cloud Storage as compressed JSON Lines files, and can
            publish every changed product to Pub/Sub every hour. Your systems read the files instead of paging through the API.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnableForm busy={busy} onEnable={enableWith} onPrepare={prepareWith} />
        </CardContent>
      </Card>
    );
  }

  if (delivery.status === 'provisioning') {
    const waitingLong = delivery.provisioningSince
      && Date.now() - new Date(delivery.provisioningSince).getTime() > 5 * 60 * 1000;
    return view(
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Creating your bucket…</CardTitle>
          <CardDescription>
            This usually takes less than a minute. Changes are delivered from the moment it is ready; a full export runs when you request one.
          </CardDescription>
        </CardHeader>
        {waitingLong && (
          <CardContent>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Requested at {formatDate(delivery.provisioningSince)} and not picked up yet: the delivery worker may be
                stopped. Contact PageMind support if this does not move.
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>
    );
  }

  if (delivery.status === 'provisioning_failed') {
    return view(
      <Card>
        <CardHeader>
          <CardTitle>Google bucket synchronization could not be set up</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="break-words">{delivery.lastError || 'Unknown error'}</AlertDescription>
          </Alert>
          <Button onClick={() => act('enable', () => bucketDeliveryApi.enable(siteId))} disabled={busy !== null}>
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (delivery.status === 'preparing' && delivery.client) {
    // Phase 2.1 (#7015): stored, not checked. The setup card, Check and enable, and a way back out.
    return view(
      <div className="space-y-4">
        <ClientDestinationCard siteId={siteId} delivery={delivery} busy={busy} act={act} copy={copy} copiedId={copiedId} />
        <Card>
          <CardHeader>
            <CardTitle>Change or cancel</CardTitle>
            <CardDescription>
              A destination does not change once prepared. To fix a value, turn this off and enable it again: the form keeps what you entered.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => act('disable', () => bucketDeliveryApi.disable(siteId))} disabled={busy !== null}>
              Turn off
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (delivery.status === 'disabled' && mode === 'client' && delivery.client) {
    // A client destination: nothing of the client's is deleted. Enabling again opens the form with the
    // previous values, editable (phase 2.1, decision 5); it is prepared and checked again. Phase 2.3: the
    // store's account is kept for 14 days (same grants), then deleted (a new one, granted again).
    const c = delivery.client;
    return view(
      <Card>
        <CardHeader>
          <CardTitle>Google bucket synchronization is off</CardTitle>
          <CardDescription>
            Turned off on {formatDate(delivery.disabledAt)}. Nothing is deleted: the files already written stay in
            gs://{c.bucket}/{c.prefix}, and nothing more is written or published.
          </CardDescription>
          <CardDescription>
            {c.serviceAccountState === 'deleted'
              ? <>This store's Google service account was deleted. Enabling again creates a new one, and you grant it again.</>
              : c.serviceAccountState === 'deletion_pending'
                ? <>This store's Google service account{c.releasedServiceAccount ? <> <code className="break-all">{c.releasedServiceAccount}</code></> : null} is being deleted (deletion pending). Enabling again creates a new one, and you grant it again.</>
                : c.serviceAccountState === 'deletion_refused'
                  ? <>We did not delete this store's Google service account{c.releasedServiceAccount ? <> <code className="break-all">{c.releasedServiceAccount}</code></> : null} (deletion refused): {c.serviceAccountDeletionRefused} It may still exist. Enabling again creates a new one, and you grant it again.</>
                  : <>This store's Google service account{c.serviceAccount ? <> <code className="break-all">{c.serviceAccount}</code></> : null} is kept for {delivery.limits.disableGraceDays} days after turning off, so enabling again within that time needs no new grant; after that it is deleted.</>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {reEnableOpen ? (
            <EnableForm
              busy={busy}
              onEnable={enableWith}
              onPrepare={prepareWith}
              initial={{ mode: 'client', projectId: c.projectId, bucket: c.bucket, prefix: c.prefix, topic: c.topic }}
            />
          ) : (
            <Button onClick={() => setReEnableOpen(true)} disabled={busy !== null}>
              <Cloud className="h-4 w-4 mr-2" /> Enable again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (delivery.status === 'disabled') {
    // Three states, never one sentence for all of them (codex review of #6944): kept for the grace
    // period, being deleted (due, or started), and deleted. A failed deletion says so: until it
    // succeeds the files may still be readable, and enabling again is refused.
    const deleting = !delivery.bucketDeletedAt && (
      Boolean(delivery.bucketDeletionStartedAt)
      || (delivery.bucketDeletionAt ? new Date(delivery.bucketDeletionAt).getTime() <= Date.now() : false)
    );
    // Codex review round 5 of #6989: while the hosted topics still exist (grace period), subscriptions
    // attached to them can still read the messages they keep, and removing an account does not stop
    // them: the subscriptions list with Detach stays available while the site is turned off.
    const topicsRemain = Boolean(delivery.pubsub?.topicsReadyAt) && !delivery.bucketDeletedAt;
    return view(
      <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Google bucket synchronization is off</CardTitle>
          <CardDescription>
            Turned off on {formatDate(delivery.disabledAt)}.{' '}
            {delivery.bucketDeletedAt
              ? `The bucket was deleted on ${formatDate(delivery.bucketDeletedAt)}.`
              : deleting
                ? `The bucket is being deleted (due ${formatDate(delivery.bucketDeletionAt)}). Until that is done, its files may still be readable.`
                : `The files stay readable until ${formatDate(delivery.bucketDeletionAt)}, then the bucket is deleted.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {delivery.lastError && !delivery.bucketDeletedAt && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="break-words">
                {delivery.lastError} It is retried automatically; until the bucket is deleted, the accounts that had access
                may still read its files.
              </AlertDescription>
            </Alert>
          )}
          {!delivery.bucketDeletedAt && (delivery.accounts || []).length > 0 && (
            // Readers keep their access until the bucket is deleted (codex review of #6944): removing
            // one must not require enabling again, which would also resume the deliveries.
            <div className="space-y-3">
              <p className="text-sm font-medium">Accounts that can still read the files</p>
              {(delivery.accounts || []).map(account => (
                <AccountRow key={account.principal} account={account} disabled={busy !== null} onRemove={() => removeAccount(account)} />
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => act('enable', () => bucketDeliveryApi.enable(siteId))}
              disabled={busy !== null || Boolean(delivery.bucketDeletionStartedAt)}
            >
              <Cloud className="h-4 w-4 mr-2" /> Enable again
            </Button>
            {delivery.bucketDeletionStartedAt && (
              <span className="text-sm text-muted-foreground">
                Available once the deletion is done: enabling again then creates a new bucket.
              </span>
            )}
          </div>
          {delivery.bucketDeletedAt && (
            // The hosted bucket is gone: the store may now choose its own project instead.
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">Enable with another destination</summary>
              <div className="pt-3"><EnableForm busy={busy} onEnable={enableWith} onPrepare={prepareWith} /></div>
            </details>
          )}
        </CardContent>
      </Card>
      {topicsRemain && <HostedPubSubCard siteId={siteId} delivery={delivery} busy={busy} act={act} copy={copy} copiedId={copiedId} turnedOff />}
      </div>
    );
  }

  // enabled
  const accounts = delivery.accounts || [];
  const active = delivery.activeRun;
  const latest = delivery.latestFull;
  const lastFinished = delivery.lastFinishedRun;
  const allowedAt = delivery.fullSyncAllowedAt ? new Date(delivery.fullSyncAllowedAt) : null;
  const coolingDown = allowedAt !== null && allowedAt.getTime() > Date.now();
  const bucketUrl = delivery.bucketUrl || '';
  const accountSlots = accounts.filter(a => a.iamStatus !== 'pending_remove').length;
  const typeInfo = ACCOUNT_TYPES.find(t => t.value === accountType) || ACCOUNT_TYPES[0];
  const pubsub = delivery.pubsub;
  const client = mode === 'client' ? delivery.client || null : null;
  // Phase 2 layout (both modes): full/<YYYY-MM-DD>-<run id first 8>/part-NNNN.jsonl.gz; phase 1 runs kept full/<run id>/.
  const folderPattern = delivery.layout?.fullFolder || 'full/<YYYY-MM-DD>-<run id first 8>/';
  const exampleFolder = latest?.folderUrl || `${bucketUrl}/${folderPattern}`;

  const fullSyncsCard = (
    <Card>
      <CardHeader>
        <CardTitle>Full exports</CardTitle>
        <CardDescription>
          A full export runs only when you request one, at most once every {limits.fullSyncMinIntervalHours} hours.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {latest ? (
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium">Latest full export:</span>{' '}
              {formatDate(latest.finishedAt)} · {latest.products.toLocaleString()} products · {latest.files} files ·{' '}
              {formatBytes(latest.bytesGzip)}
            </div>
            {(latest.governance.refused > 0 || latest.governance.partialLanguageProducts > 0) && (
              <div className="text-sm text-amber-700 dark:text-amber-400">
                Publication guard:{' '}
                {[
                  latest.governance.refused > 0
                    ? `${latest.governance.refused.toLocaleString()} of ${latest.governance.withStoredGeneration.toLocaleString()} products delivered without their generated content (${Object.entries(latest.governance.conditions).map(([c, n]) => `${c}: ${n}`).join(', ')})`
                    : null,
                  latest.governance.partialLanguageProducts > 0
                    ? `${latest.governance.partialLanguageProducts.toLocaleString()} products with some languages withheld`
                    : null,
                ].filter(Boolean).join(' · ')}
                .
              </div>
            )}
            {latest.manifestUrl && (
              <CodeLine text={latest.manifestUrl} onCopy={() => copy(latest.manifestUrl as string, 'manifest')} copied={copiedId === 'manifest'} />
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No full export yet.</p>
        )}

        {active && <RunStatusLine run={active} />}
        {!active && lastFinished && lastFinished.status === 'failed' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="break-words">
              The last full export failed on {formatDate(lastFinished.finishedAt)}: {lastFinished.error}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => act('full-sync', () => bucketDeliveryApi.runFullSync(siteId), 'Full export queued')}
            disabled={busy !== null || Boolean(active) || coolingDown}
          >
            {busy === 'full-sync' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Request full export
          </Button>
          {(active || coolingDown) && (
            <span className="text-sm text-muted-foreground">
              {active ? 'A full export is in progress.' : `Available again at ${formatDate(delivery.fullSyncAllowedAt)}.`}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Products that exist today are delivered only by a full export. Request one to start from a complete copy.
        </p>
      </CardContent>
    </Card>
  );

  // Hourly deltas, both modes (spec items 6-9), once a topic exists.
  const deltasCard = pubsub && pubsub.deltasFlowing ? (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5" /> Hourly changes on Pub/Sub</CardTitle>
        <CardDescription>
          Every {pubsub.deltaIntervalMinutes} minutes, one message per product that changed: the gzip of the same product object
          a file line holds, with the attributes <code>site</code>, <code>store</code>, <code>sku</code>, <code>item_group_id</code>,{' '}
          <code>shape_version</code>, <code>payload_hash</code> (SHA-256 of the uncompressed object), <code>run_id</code> and{' '}
          <code>run_seq</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {pubsub.topic && <CodeLine text={pubsub.topic} onCopy={() => copy(pubsub.topic as string, 'topic')} copied={copiedId === 'topic'} />}
        {pubsub.activeDelta && <DeltaStatusLine run={pubsub.activeDelta} label="Current" />}
        {pubsub.lastDelta && <DeltaStatusLine run={pubsub.lastDelta} label="Last" />}
        <div className="grid gap-1 md:grid-cols-2">
          <span>Next: {pubsub.activeDelta ? 'after the current one' : formatDate(pubsub.nextDeltaAt)}</span>
          <span>Published up to: {formatDate(pubsub.publishWatermark)}{pubsub.runSeq ? ` (run_seq ${pubsub.runSeq})` : ''}</span>
        </div>
      </CardContent>
    </Card>
  ) : null;

  const turnOffCard = (
    <Card>
      <CardHeader>
        <CardTitle>Turn off</CardTitle>
        <CardDescription>
          {client
            ? 'Stops all deliveries. Nothing is deleted from your bucket.'
            : `Stops all deliveries. The files stay readable for ${limits.disableGraceDays} days, then the bucket and its topics are deleted.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={busy !== null}>Turn off Google bucket synchronization</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Turn off Google bucket synchronization?</AlertDialogTitle>
              <AlertDialogDescription>
                {client
                  ? `No new files will be written to gs://${client.bucket}/${client.prefix} and no changes published. The files already there stay.`
                  : `No new files will be written. ${bucketUrl} and its files are deleted in ${limits.disableGraceDays} days unless you enable it again before then.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => act('disable', () => bucketDeliveryApi.disable(siteId), 'Google bucket synchronization turned off')}>
                Turn off
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );

  if (client) {
    return view(
      <div className="space-y-4">
        <ClientDestinationCard
          siteId={siteId}
          delivery={delivery}
          busy={busy}
          act={act}
          copy={copy}
          copiedId={copiedId}
        />
        {fullSyncsCard}
        {deltasCard}
        <Card>
          <CardHeader>
            <CardTitle>What we write</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ConsumerNotes changes={Boolean(pubsub?.deltasFlowing)} />
            <p>
              Each full export lands in <code>{folderPattern}</code> as files <code>part-0000.jsonl.gz</code>,{' '}
              <code>part-0001.jsonl.gz</code>, … of about {limits.productsPerFile.toLocaleString()} products each: gzip-compressed
              JSON Lines, one product per line, the same object the export API returns (delivery shape v{delivery.shapeVersion}).
            </p>
            <p>
              A file appears only once it is complete, so your bucket notification can start one worker per file as it lands.
              <code> manifest.json</code> is written last in the same folder, with every file's product count and SHA-256: optional
              to read, useful to check you received everything.
            </p>
            <p>
              To be told about each new file, create a notification on <code>{client.prefix}full/</code> of your bucket to a topic
              of yours {NOTIFICATION_WHERE}:
            </p>
            <CodeLine
              text={notificationCommand(client.bucket, client.projectId, client.prefix)}
              onCopy={() => copy(notificationCommand(client.bucket, client.projectId, client.prefix), 'notification')}
              copied={copiedId === 'notification'}
            />
            <p>{CLIENT_FILE_NOTIFICATIONS}</p>
            <p>
              We also use <code>{delivery.layout?.staging || `${client.prefix}_pagemind/`}</code> for work in progress and the destination
              check; set your notification on <code>{client.prefix}full/</code> to ignore it.
            </p>
          </CardContent>
        </Card>
        {turnOffCard}
      </div>
    );
  }

  return view(
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" /> Google bucket
            <Badge variant="success">Enabled</Badge>
          </CardTitle>
          <CardDescription className="text-foreground">{HOSTED_OVERVIEW}</CardDescription>
          <CardDescription>
            A private, Requester Pays bucket in {delivery.location || 'the EU'}. A full export of your catalogue is written each time you request one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <CodeLine text={bucketUrl} onCopy={() => copy(bucketUrl, 'bucket')} copied={copiedId === 'bucket'} />
          <p className="text-sm text-muted-foreground">
            Each full export gets its own folder, <code>{folderPattern}</code>, with files of about{' '}
            {limits.productsPerFile.toLocaleString()} products (<code>part-0000.jsonl.gz</code>, <code>part-0001.jsonl.gz</code>, …) and a{' '}
            <code>manifest.json</code> written last.
          </p>
        </CardContent>
      </Card>

      {fullSyncsCard}

      {pubsub && <HostedPubSubCard siteId={siteId} delivery={delivery} busy={busy} act={act} copy={copy} copiedId={copiedId} />}
      {deltasCard}

      <Card>
        <CardHeader>
          <CardTitle>Accounts with access</CardTitle>
          <CardDescription>
            Read-only access to the bucket{pubsub?.topicsReadyAt ? ', and permission to attach subscriptions to both topics' : ''}{' '}
            ({accountSlots}/{limits.maxAccounts}). Removing an account revokes its access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accounts.length === 0 && (
            <p className="text-sm text-muted-foreground">No account can read the bucket yet. Add the account your loader runs as.</p>
          )}
          {accounts.map(account => (
            <AccountRow key={account.principal} account={account} disabled={busy !== null} onRemove={() => removeAccount(account)} />
          ))}

          <div className="grid gap-2 md:grid-cols-[180px_1fr_auto] md:items-end">
            <div className="space-y-1">
              <Label htmlFor="bucket-account-type">Type</Label>
              <Select value={accountType} onValueChange={(v) => setAccountType(v as AccountType)}>
                <SelectTrigger id="bucket-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bucket-account-email">Email</Label>
              <Input
                id="bucket-account-email"
                value={accountEmail}
                placeholder={typeInfo.placeholder}
                onChange={(e) => setAccountEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addAccount(); }}
              />
            </div>
            <Button onClick={addAccount} disabled={busy !== null || accountSlots >= limits.maxAccounts}>
              {busy === 'add-account' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Add
            </Button>
          </div>
          {accountError && <p className="text-sm text-destructive break-words">{accountError}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How to read the bucket</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ConsumerNotes changes={Boolean(pubsub?.deltasFlowing)} />
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              A file appears in <code>{folderPattern}</code> only once it is complete
              {pubsub?.filesTopic ? ', and the files topic announces each one as it lands' : ''}. <code>manifest.json</code> is
              written last: a folder without one is a full export still running, or one that failed.
            </li>
            <li>
              The manifest lists every file with its product count and SHA-256, so you can check you received everything
              intact, and the <code>baseline</code> time the data was read from. Its <code>governance</code> block says how
              many products were delivered without some or all of their generated content. A full export where every
              product's generated content was withheld is not delivered at all: it fails, and the previous copy stays.
            </li>
            <li>
              Each file is gzip-compressed JSON Lines: one product per line, the same product object the export API
              returns in JSON with all languages and structured AEO content (delivery shape v{delivery.shapeVersion}).
            </li>
            <li>
              Files are never replaced: a name always means the same bytes. Work in progress lives under{' '}
              <code>_pagemind/</code>, which you can ignore.
            </li>
            <li>
              The bucket is <strong>Requester Pays</strong>: every read must name a Google Cloud project to bill, or it
              fails with <code>400 UserProjectMissing</code>. The reading account needs the Service Usage Consumer role
              in that project.
            </li>
          </ol>
          <div className="bg-zinc-900 text-zinc-100 p-3 rounded-md font-mono text-xs">
            <div className="flex justify-between items-start mb-2">
              <span className="text-zinc-400">Example</span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-zinc-400 hover:text-zinc-100"
                onClick={() => copy(`gcloud storage cp --billing-project=YOUR_PROJECT "${exampleFolder}*" ./`, 'gcloud')}
              >
                {copiedId === 'gcloud' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <pre className="whitespace-pre-wrap break-all">{`gcloud storage cp --billing-project=YOUR_PROJECT "${exampleFolder}*" ./`}</pre>
          </div>
          <p className="text-muted-foreground">
            You remain responsible for telling us which products you publish with our content, and when, through the
            publication manifest API. Files from a previous full export are deleted {limits.previousFullGraceHours} hours after a newer one completes.
          </p>
        </CardContent>
      </Card>

      {turnOffCard}
    </div>
  );
}

type Act = (name: string, fn: () => Promise<{ delivery: BucketDelivery }>, success?: string) => Promise<boolean>;
type CardProps = {
  siteId: string;
  delivery: BucketDelivery;
  busy: string | null;
  act: Act;
  copy: (text: string, id: string) => void;
  copiedId: string | null;
};

/** True while the worker still has something to check (the same rule as the backend's clientCheckDue). */
function clientCheckDue(c: NonNullable<BucketDelivery['client']>): boolean {
  return !c.verifiedAt || Boolean(c.error) || (Boolean(c.topic) && !c.topicVerifiedAt);
}

/**
 * Client mode: the destination, its setup and its check (spec items 2, 3, 13). Phase 2.1 (#7015): while
 * 'preparing' it shows the setup and Check and enable, never an error; once enabled, a pending or failed
 * check offers Check now. A destination is never edited here: any change is Turn off, then Enable again
 * with the pre-filled form. Phase 2.3 (#7077): the setup is this store's own service account and the two
 * roles to grant it ("Creating the account…" until the worker has created it); the ownership file, its
 * download and the topic label are gone. A refusal right after Check and enable reads "Checking…".
 */
function ClientDestinationCard({ siteId, delivery, busy, act, copy, copiedId }: CardProps) {
  const c = delivery.client!;
  const preparing = delivery.status === 'preparing';
  const creating = c.serviceAccountState === 'creating' || (!c.serviceAccount && c.serviceAccountState !== 'deleted');
  const due = !preparing && clientCheckDue(c);
  const propagating = !preparing && Boolean(c.propagating);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Cloud className="h-5 w-5" /> Your Google Cloud project
          {preparing
            ? <Badge variant="secondary">{creating ? 'Creating the account…' : 'Preparing'}</Badge>
            : propagating
              ? <Badge variant="secondary">Checking…</Badge>
              : c.error
                ? <Badge variant="destructive">Check failed</Badge>
                : c.verifiedAt ? <Badge variant="success">Verified</Badge> : <Badge variant="secondary">Checking…</Badge>}
          {!preparing && !propagating && c.topicError && <Badge variant="destructive">{c.topicError.startsWith('Topic check failed') ? 'Topic not verified' : 'Publishing failed'}</Badge>}
        </CardTitle>
        <CardDescription className="text-foreground">{clientOverview(Boolean(c.topic))}</CardDescription>
        <CardDescription>
          {preparing
            ? (creating
              ? <>We are creating this store's own Google service account. It appears here, with the two roles to grant it, within a few seconds.</>
              : <>Grant the roles below to this store's account in project {c.projectId}, then click Check and enable. Nothing is checked, written or published until you click it.</>)
            : <>We write into gs://{c.bucket}/{c.prefix} in project {c.projectId}{c.topic ? ' and publish changes to your topic.' : '. No topic: nothing is delivered automatically; a full export is written each time you request one.'}</>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {preparing && (
          <p>
            Destination: <code className="break-all">gs://{c.bucket}/{c.prefix}</code>
            {c.topic ? <> and topic <code className="break-all">{c.topic}</code></> : ', files only (no topic)'}.
          </p>
        )}
        {propagating && (
          <Alert>
            <Loader2 className="h-4 w-4 animate-spin" />
            <AlertDescription className="space-y-1 break-words">
              <p className="font-medium">Checking: Google can take up to a minute to apply a new grant.</p>
              <p>We check again {c.nextCheckAt ? `at ${formatDate(c.nextCheckAt)}` : 'at the next pass'}, or click Check now.</p>
              {[c.error, c.topicError].filter(Boolean).map(e => <p key={e as string} className="text-muted-foreground">{e}</p>)}
            </AlertDescription>
          </Alert>
        )}
        {!preparing && !propagating && c.error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-1 break-words">
              {c.errorSummary && <p className="font-medium">{c.errorSummary}</p>}
              <p>{c.error}</p>
              <p>Nothing is written until this works.</p>
            </AlertDescription>
          </Alert>
        )}
        {!preparing && !propagating && c.topicError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="break-words">
              Topic {c.topic}: {c.topicError} Check the grants below; nothing is published until this is fixed.
            </AlertDescription>
          </Alert>
        )}
        {due && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => act('check-now', () => bucketDeliveryApi.checkNow(siteId))}
              disabled={busy !== null || Boolean(c.checkRequestedAt)}
            >
              {busy === 'check-now' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Check now
            </Button>
            <span className="text-muted-foreground">
              {c.checkRequestedAt
                ? 'Check requested: the worker checks again within 15 seconds.'
                : c.checkedAt
                  ? `Last checked at ${formatDate(c.checkedAt)} · next automatic check at ${c.nextCheckAt ? formatDate(c.nextCheckAt) : 'its next pass'}`
                  : 'Not checked yet: the first check runs within a few seconds.'}
            </span>
          </div>
        )}
        {!preparing && !due && c.verifiedAt && <p className="text-muted-foreground">Last check: {formatDate(c.verifiedAt)}.</p>}
        {creating ? (
          <div className="space-y-2">
            <p className="font-medium flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Creating the account…</p>
            {c.serviceAccountError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="break-words">{c.serviceAccountError} We try again within 5 minutes.</AlertDescription>
              </Alert>
            )}
          </div>
        ) : c.serviceAccount ? (
          <div className="space-y-2">
            <p className="font-medium">Grant these roles, once, to this store's own service account:</p>
            <CodeLine text={c.serviceAccount} onCopy={() => copy(c.serviceAccount as string, 'sa')} copied={copiedId === 'sa'} />
            <ul className="list-disc pl-5 space-y-3">
              {c.grants.map((g, i) => (
                <li key={g.role} className="space-y-1">
                  <p><code>{g.role}</code> on <code className="break-all">{g.resource}</code></p>
                  <CodeLine text={g.command} onCopy={() => copy(g.command, `grant-${i}`)} copied={copiedId === `grant-${i}`} />
                  <p className="text-muted-foreground">Console: {g.consolePath}</p>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground">
              Only this store uses this account, and it has no key: your grant is what lets it write here. Nothing else: we
              never read your other data, and you control who reads what we write.
            </p>
            {/* only while Check now is offered (a check is due): a publishing error on a verified topic has no Check now */}
            {due && !propagating && (c.error || c.topicError) && (
              <p className="font-medium">
                Still failing after a few minutes? Compare the grants above with the Permissions page of your bucket
                {c.topic ? ' and topic' : ''}, then click Check now.
              </p>
            )}
          </div>
        ) : null}
        {preparing ? (
          <Button onClick={() => act('check-and-enable', () => bucketDeliveryApi.checkAndEnable(siteId))} disabled={busy !== null || creating}>
            {busy === 'check-and-enable' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Check and enable
          </Button>
        ) : (
          <p className="text-muted-foreground">
            To change it, turn synchronization off and enable it again: the form keeps your values, and the new destination is checked again.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** Hosted mode: our two topics, the subscriptions attached to them with Detach, and the seek time (spec item 16). */
function HostedPubSubCard({ siteId, delivery, busy, act, copy, copiedId, turnedOff = false }: CardProps & { turnedOff?: boolean }) {
  const p = delivery.pubsub!;
  if (!p.topicsReadyAt) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5" /> Pub/Sub</CardTitle>
          <CardDescription>Creating the files and changes topics of this bucket…</CardDescription>
        </CardHeader>
        {delivery.lastError && (
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="break-words">{delivery.lastError} It is retried automatically.</AlertDescription>
            </Alert>
          </CardContent>
        )}
      </Card>
    );
  }
  const subscriptions = p.subscriptions || [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5" /> Pub/Sub topics</CardTitle>
        <CardDescription>
          {turnedOff
            ? <>Synchronization is off: nothing new is published. Both topics are deleted with the bucket{delivery.bucketDeletionAt ? ` on ${formatDate(delivery.bucketDeletionAt)}` : ''}. Until then, subscriptions attached to them can still read the messages the topics keep: detach any that should lose access now.</>
            : <>Attach a subscription from your own project to either topic (your project is billed for it). The files topic announces
              every new file in <code>full/</code>; the changes topic carries the hourly changes. Both keep messages {p.topicRetentionDays} days.</>}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-2">
          <p className="font-medium">Files topic</p>
          {p.filesTopic && <CodeLine text={p.filesTopic} onCopy={() => copy(p.filesTopic as string, 'files-topic')} copied={copiedId === 'files-topic'} />}
          <p className="text-muted-foreground">{FILES_TOPIC_NOTIFICATIONS}</p>
          <p className="font-medium">Changes topic</p>
          {p.topic && <CodeLine text={p.topic} onCopy={() => copy(p.topic as string, 'deltas-topic')} copied={copiedId === 'deltas-topic'} />}
        </div>
        {!turnedOff && p.seekFrom && (
          <p>
            A subscription created late can replay the changes it missed: seek it to <strong>{formatDate(p.seekFrom)}</strong>{' '}
            (<code>{p.seekFrom}</code>). Further back than that, start from the latest full export.
          </p>
        )}
        <div className="space-y-2">
          <p className="font-medium">Attached subscriptions</p>
          {subscriptions.length === 0 && <p className="text-muted-foreground">None{p.subscriptionsRefreshedAt ? ` (as of ${formatDate(p.subscriptionsRefreshedAt)})` : ''}.</p>}
          {subscriptions.map(s => (
            <div key={s.name} className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0">
              <div className="min-w-0">
                <span className="font-mono break-all">{s.name}</span>{' '}
                <Badge variant="outline">{s.topic === p.filesTopic ? 'files' : 'changes'}</Badge>{' '}
                {s.detachPending && <Badge variant="secondary">Detaching…</Badge>}
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" aria-label={`Detach ${s.name}`} disabled={busy !== null || s.detachPending}>
                    <Unlink className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Detach this subscription?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {s.name} stops receiving messages from our topic for good. This cannot be undone: to read again, its
                      project creates a new subscription.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => act(`detach-${s.name}`, () => bucketDeliveryApi.detachSubscription(siteId, s.name), 'Detach requested')}>
                      Detach
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
          <p className="text-muted-foreground">
            Removing an account below does not stop a subscription it already created (Google's behaviour): detach it here.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default GoogleBucketTab;
